import {
  WSButton,
  WSElement,
  WSFlexBox,
  WSLoader,
  WSPage,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { ReactNode, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { WSQueries } from "../../../../query/WSQuery";
import {
  useMutationCountersignDocument,
  useMutationSignDocument
} from "../../../../query/files/mutations";
import { useUserId } from "../../../../query/hooks/helpers";
import { useQueryNotifications } from "../../../../query/notifications/queries/useQueryNotifications";
import { buildPayeeDetailsPath } from "../../../Payees/utils";
import { selectorSignatureClientNotifications } from "../../selectors/selectorSignatureClientNotifications";
import { selectorSignatureCollaboratorNotifications } from "../../selectors/selectorSignatureCollaboratorNotifications";
import { useTrackClickNotification } from "../../utils/useTrackClickNotification";
import { useGoToDashboardPayerRequirementDetails } from "../RouteRequirementDetails/RoutePayerRequirementDetails";
import { useGoToDashboardPayeeRequirementDetails } from "../RouteRequirementDetails/RoutePayeeRequirementDetails";

type DocumentToSign = {
  notificationId: string;
  title: string;
  description: ReactNode;
  actionTitle: string;
  onClick: () => Promise<void>;
};

export const RouteDocumentsToSign: React.FC<{
  type: "client" | "collaborator";
}> = ({ type }) => {
  const history = useHistory();
  const userId = useUserId();
  const trackClickNotification = useTrackClickNotification(userId);
  const queryNotifications = useQueryNotifications();

  const goToPayerRequirementDetails = useGoToDashboardPayerRequirementDetails();
  const goToPayeeRequirementDetails = useGoToDashboardPayeeRequirementDetails();

  const [countersign] = useMutationCountersignDocument();
  const [signDocument] = useMutationSignDocument();

  const documents = useMemo(() => {
    if (!queryNotifications.data) {
      return [];
    }

    const documents: DocumentToSign[] = [];

    if (type === "collaborator") {
      const notifications = selectorSignatureCollaboratorNotifications(
        queryNotifications.data
      );
      notifications.forEach(notification => {
        const clientName = notification.context?.payer?.payerName;
        const documentName = notification.context?.requirement?.name;

        documents.push({
          notificationId: notification.notificationId,
          title: documentName || "",
          description: `${clientName} requires that you sign this document to complete the onboarding process`,
          actionTitle: "Sign document",
          onClick: async () => {
            trackClickNotification(notification);

            goToPayerRequirementDetails({
              payerId: notification.context?.payer.payerId,
              requirementId: notification.context?.requirement?.requirementId
            });
          }
        });
      });
    } else {
      const notifications = selectorSignatureClientNotifications(
        queryNotifications.data
      );
      notifications.forEach(notification => {
        const documentName = notification.context?.requirement?.name;

        documents.push({
          notificationId: notification.notificationId,
          title: documentName || "",
          description: (
            <>
              Your contractor{" "}
              <a
                onClick={() => {
                  history.push(
                    buildPayeeDetailsPath(notification.context?.payee.payeeId)
                  );
                }}
              >
                <b>{notification.context?.payee.payeeName}</b>
              </a>{" "}
              already signed this document
            </>
          ),
          actionTitle: "Countersign",
          onClick: async () => {
            trackClickNotification(notification);
            goToPayeeRequirementDetails({
              payeeId: notification.context?.payee.payeeId,
              requirementId: notification.context.requirement?.requirementId
            });
          }
        });
      });
    }

    return documents;
  }, [
    countersign,
    history,
    queryNotifications.data,
    signDocument,
    trackClickNotification,
    type
  ]);

  if (queryNotifications.isLoading) {
    return <WSLoader.Spinner />;
  }

  return (
    <WSQueries queries={{ queryNotifications }}>
      {() => {
        return (
          <WSPage
            breadcrumb={{
              label: "Back",
              onClick() {
                history.push("/member/dashboard");
              },
              icon: "arrow-left"
            }}
          >
            <WSText.Heading4 my="XL">
              E-signatures required ({documents.length})
            </WSText.Heading4>
            {documents.length === 0 ? (
              <WSText.ParagraphSm mt="3XL" align="center" color="gray400">
                No documents to sign
              </WSText.ParagraphSm>
            ) : (
              documents.map(
                ({
                  notificationId,
                  title,
                  onClick,
                  description,
                  actionTitle
                }) => {
                  return (
                    <WSPanel key={notificationId} mb="M">
                      <WSFlexBox.CenterY justify="space-between" gap="M">
                        <WSElement>
                          <WSText weight="medium" pb="M">
                            {title}
                          </WSText>
                          <WSText.ParagraphSm>{description}</WSText.ParagraphSm>
                        </WSElement>
                        <WSButton.Secondary size="S" onAsyncClick={onClick}>
                          {actionTitle}
                        </WSButton.Secondary>
                      </WSFlexBox.CenterY>
                    </WSPanel>
                  );
                }
              )
            )}
          </WSPage>
        );
      }}
    </WSQueries>
  );
};
